import React from 'react';
import {useParams,Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import {useCMS} from '../context/CMSContext';
import SafeIcon from '../common/SafeIcon';
import ServiceFAQs from '../components/ServiceFAQs';
import {serviceFAQs} from '../data/serviceFAQs';
import * as FiIcons from 'react-icons/fi';

const {FiArrowLeft,FiCheckCircle,FiTrendingUp,FiArrowRight,FiStar,FiUsers}=FiIcons;

const ServiceDetail=()=> {
  const {serviceId}=useParams();
  const {content}=useCMS();
  const {services}=content;
  const service=services.items.find((s)=> s.id===serviceId);

  // Extended testimonials for each service
  const serviceTestimonials={
    assainissement: [
      {name: 'Marie Dupont',company: 'Industries Alimentaires SA',text: 'Un système d\'assainissement parfaitement conçu et installé. Nos clients ont remarqué l\'amélioration immédiatement.',},{name: 'Paul Kamdem',company: 'Plantations Agro',text: 'Service professionnel et rapide. L\'équipe a compris nos contraintes et a livré une solution adaptée.',},],
    hygiene: [
      {name: 'Dr. Jean Mbarga',company: 'Clinique Moderne',text: 'Désinfection régulière et efficace selon les normes du MINSANTE. Nos patients se sentent en sécurité.',},{name: 'Sophie Martin',company: 'Restaurant Le Prestige',text: 'Conformité garantie aux normes d\'hygiène. Les inspections sont désormais sans problème.',},],
    'controle-pollution': [
      {name: 'Eng. Robert Talla',company: 'Usine Textile Douala',text: 'Analyses précises et rapides. Nous pouvons maintenant justifier notre conformité environnementale.',},{name: 'Véronique Nkoto',company: 'Cimenterie du Cameroun',text: 'Suivi régulier de la qualité de l\'air. Les données nous aident à optimiser nos processus.',},],
    formation: [
      {name: 'Directeur RH',company: 'Groupe Industriel XYZ',text: 'Formations de qualité qui ont transformé la culture sécurité de notre entreprise.',},{name: 'Coordinatrice QHSE',company: 'Entreprise de Construction',text: 'Les stagiaires ont trouvé les formations très pratiques et applicables immédiatement.',},],
    'support-qhse': [
      {name: 'Directeur Général',company: 'PME Manufacturière',text: 'Accompagnement jusqu\'à la certification ISO 14001. Nos clients font confiance à notre engagement environnemental.',},{name: 'Manager QHSE',company: 'Entreprise Export',text: 'Support continu et expertise pour maintenir et améliorer nos certifications.',},],
  };

  if (!service) {
    return (
      <div className="min-h-screen py-20 bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Service non trouvé
          </h1>
          <Link to="/services" className="text-pink-600 hover:text-pink-700">
            Retour aux services
          </Link>
        </div>
      </div>
    );
  }

  const testimonials=serviceTestimonials[serviceId] || [];
  const faqs=serviceFAQs[serviceId] || [];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className={`py-20 text-white relative overflow-hidden`}>
        <div className={`absolute inset-0 ${service.color} opacity-90`}></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0,y: 50}}
            animate={{opacity: 1,y: 0}}
          >
            <Link
              to="/services"
              className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-2" />
              Retour aux services
            </Link>
            <div className="flex items-center space-x-6 mb-8">
              <motion.div
                className={`w-24 h-24 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center`}
                whileHover={{scale: 1.1}}
              >
                <SafeIcon
                  icon={FiIcons[service.icon]}
                  className="w-12 h-12 text-white"
                />
              </motion.div>
              <div>
                <h1 className="text-5xl lg:text-6xl font-bold mb-4">
                  {service.title}
                </h1>
                <p className="text-xl text-white/90">{service.description}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Services List */}
              <motion.div
                initial={{opacity: 0,y: 50}}
                animate={{opacity: 1,y: 0}}
                className="mb-12"
              >
                <h2 className="text-4xl font-bold text-gray-900 mb-8 bg-gradient-to-r from-pink-600 to-blue-600 bg-clip-text text-transparent">
                  Nos Services
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {service.details.services.map((item,index)=> (
                    <motion.div
                      key={index}
                      initial={{opacity: 0,x: -20}}
                      animate={{opacity: 1,x: 0}}
                      transition={{delay: index * 0.1}}
                      className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all"
                      whileHover={{x: 10}}
                    >
                      <SafeIcon
                        icon={FiCheckCircle}
                        className="w-6 h-6 text-green-500 mt-1 flex-shrink-0"
                      />
                      <span className="text-gray-700 font-medium">{item}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Benefits */}
              <motion.div
                initial={{opacity: 0,y: 50}}
                animate={{opacity: 1,y: 0}}
                transition={{delay: 0.3}}
              >
                <h2 className="text-4xl font-bold text-gray-900 mb-8 bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
                  Avantages
                </h2>
                <div className="space-y-4">
                  {service.details.benefits.map((benefit,index)=> (
                    <motion.div
                      key={index}
                      initial={{opacity: 0,x: -20}}
                      animate={{opacity: 1,x: 0}}
                      transition={{delay: 0.4 + index * 0.1}}
                      className="flex items-start space-x-3 p-4 bg-gradient-to-r from-pink-50 to-blue-50 rounded-lg hover:shadow-lg transition-all"
                      whileHover={{x: 10}}
                    >
                      <SafeIcon
                        icon={FiTrendingUp}
                        className="w-6 h-6 text-pink-500 mt-1 flex-shrink-0"
                      />
                      <span className="text-gray-700 font-medium">
                        {benefit}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{opacity: 0,x: 50}}
                animate={{opacity: 1,x: 0}}
                transition={{delay: 0.2}}
                className="bg-gray-50 rounded-2xl p-8 sticky top-32"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Demander un devis
                </h3>
                <p className="text-gray-600 mb-6">
                  Obtenez une évaluation personnalisée pour ce service adapté à
                  vos besoins spécifiques.
                </p>
                <div className="space-y-4 mb-8">
                  <div className="flex items-center space-x-3">
                    <SafeIcon
                      icon={FiCheckCircle}
                      className="w-5 h-5 text-green-500"
                    />
                    <span className="text-gray-700">Consultation gratuite</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <SafeIcon
                      icon={FiCheckCircle}
                      className="w-5 h-5 text-green-500"
                    />
                    <span className="text-gray-700">Devis sous 24h</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <SafeIcon
                      icon={FiCheckCircle}
                      className="w-5 h-5 text-green-500"
                    />
                    <span className="text-gray-700">Expert dédié</span>
                  </div>
                </div>
                <Link
                  to="/contact"
                  className="w-full bg-pink-600 text-white px-6 py-4 rounded-lg font-semibold hover:bg-pink-700 transition-colors inline-flex items-center justify-center"
                >
                  Demander un devis
                  <SafeIcon icon={FiArrowRight} className="ml-2 w-4 h-4" />
                </Link>
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-3">
                    Besoin d'aide ?
                  </h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Contactez directement nos experts pour toute question.
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="text-gray-600">📞 +237 675 78 46 56</div>
                    <div className="text-gray-600">
                      ✉️ contact@qhseconsulting.biz
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{opacity: 0,y: 50}}
              whileInView={{opacity: 1,y: 0}}
              viewport={{once: true}}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4 bg-gradient-to-r from-blue-600 to-pink-600 bg-clip-text text-transparent">
                Témoignages de nos clients
              </h2>
              <p className="text-gray-600">
                Découvrez comment nous avons aidé nos clients pour ce service
              </p>
            </motion.div>
            <motion.div
              initial={{opacity: 0}}
              whileInView={{opacity: 1}}
              viewport={{once: true}}
              className="grid grid-cols-1 lg:grid-cols-2 gap-8"
            >
              {testimonials.map((testimonial,index)=> (
                <motion.div
                  key={index}
                  initial={{opacity: 0,y: 20}}
                  whileInView={{opacity: 1,y: 0}}
                  viewport={{once: true}}
                  transition={{delay: index * 0.2}}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all"
                  whileHover={{y: -5}}
                >
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_,i)=> (
                      <SafeIcon
                        key={i}
                        icon={FiStar}
                        className="w-5 h-5 text-yellow-400 fill-current"
                      />
                    ))}
                  </div>
                  <p className="text-gray-600 mb-6 italic text-lg">
                    "{testimonial.text}"
                  </p>
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-blue-500 rounded-full flex items-center justify-center">
                      <SafeIcon
                        icon={FiUsers}
                        className="w-6 h-6 text-white"
                      />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">
                        {testimonial.name}
                      </h4>
                      <p className="text-gray-600 text-sm">
                        {testimonial.company}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* FAQs Section */}
      {faqs.length > 0 && (
        <ServiceFAQs
          faqs={faqs}
          serviceTitle={service.title}
          serviceColor={service.color}
        />
      )}

      {/* Related Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0,y: 50}}
            whileInView={{opacity: 1,y: 0}}
            viewport={{once: true}}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4 bg-gradient-to-r from-pink-600 to-green-600 bg-clip-text text-transparent">
              Autres Services
            </h2>
            <p className="text-gray-600">
              Découvrez nos autres domaines d'expertise
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.items
              .filter((s)=> s.id !==serviceId)
              .slice(0,4)
              .map((relatedService,index)=> (
                <motion.div
                  key={relatedService.id}
                  initial={{opacity: 0,y: 50}}
                  whileInView={{opacity: 1,y: 0}}
                  viewport={{once: true}}
                  transition={{delay: index * 0.1}}
                >
                  <Link
                    to={`/services/${relatedService.id}`}
                    className="block bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all group"
                    whileHover={{y: -5}}
                  >
                    <div
                      className={`w-14 h-14 ${relatedService.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}
                    >
                      <SafeIcon
                        icon={FiIcons[relatedService.icon]}
                        className="w-7 h-7 text-white"
                      />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2">
                      {relatedService.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {relatedService.description.substring(0,80)}...
                    </p>
                  </Link>
                </motion.div>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;